import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hubFetch } from "../lib/auth.js";

export type SkillState = "enabled" | "disabled" | "removed" | "deprecated";

export interface PackageRow {
  name: string;
  family: "skill" | "code-plugin" | "bundle-plugin";
  channel: "official" | "community" | "private";
  latest_version: string | null;
  created_at: number;
  updated_at: number;
  download_count: number;
  star_count: number;
  yanked: number;
  /** 是否纳入 mhwork 平台远程管控,默认 0 — admin 显式 toggle */
  managed: number;
  /** latest_version 那一行的 state(列表 LEFT JOIN 来),没有 latest 时为 null */
  latest_state?: SkillState | null;
}

export interface VersionRow {
  name: string;
  version: string;
  family: PackageRow["family"];
  channel: PackageRow["channel"];
  display_name: string | null;
  description: string | null;
  changelog: string;
  tags_json: string | null;
  files_json: string;
  published_at: number;
  published_by: string;
  yanked: number;
  state: SkillState;
}

export function usePackages() {
  return useQuery({
    queryKey: ["admin", "packages"],
    queryFn: async () => {
      const res = await hubFetch<{ items: PackageRow[] }>("/admin/packages");
      return res.items;
    },
  });
}

export function usePackageDetail(name: string | undefined) {
  return useQuery({
    queryKey: ["admin", "package", name],
    queryFn: async () => {
      if (!name) throw new Error("missing name");
      return hubFetch<{ package: PackageRow; versions: VersionRow[] }>(
        `/admin/packages/${encodeURIComponent(name)}`,
      );
    },
    enabled: !!name,
  });
}

export function useYankPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, yank }: { name: string; yank: boolean }) =>
      hubFetch(`/admin/packages/${encodeURIComponent(name)}/${yank ? "yank" : "unyank"}`, {
        method: "POST",
      }),
    onSuccess: (_d, { name }) => {
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
      qc.invalidateQueries({ queryKey: ["admin", "package", name] });
    },
  });
}

export function useYankVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, version }: { name: string; version: string }) =>
      hubFetch(
        `/admin/packages/${encodeURIComponent(name)}/versions/${encodeURIComponent(version)}/yank`,
        { method: "POST" },
      ),
    onSuccess: (_d, { name }) => {
      qc.invalidateQueries({ queryKey: ["admin", "package", name] });
    },
  });
}

export function useDeletePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      hubFetch<{ ok: true; deletedVersions: number; deletedObjects: number }>(
        `/admin/packages/${encodeURIComponent(name)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
    },
  });
}

/**
 * 改版本级展示字段(displayName / description / tags / changelog)。
 * 不改 zip / sha256 / version 本身。
 */
export function usePatchVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      name: string;
      version: string;
      patch: {
        displayName?: string | null;
        description?: string | null;
        tags?: string[] | null;
        changelog?: string | null;
      };
    }) =>
      hubFetch(
        `/admin/packages/${encodeURIComponent(args.name)}/versions/${encodeURIComponent(args.version)}`,
        {
          method: "PATCH",
          body: JSON.stringify(args.patch),
        },
      ),
    onSuccess: (_d, { name }) => {
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
      qc.invalidateQueries({ queryKey: ["admin", "package", name] });
    },
  });
}

/** 改包级字段(channel / managed,owner 独占) */
export function usePatchPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      name: string;
      channel?: PackageRow["channel"];
      managed?: boolean;
    }) =>
      hubFetch(`/admin/packages/${encodeURIComponent(args.name)}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...(args.channel !== undefined ? { channel: args.channel } : {}),
          ...(args.managed !== undefined ? { managed: args.managed } : {}),
        }),
      }),
    onSuccess: (_d, { name }) => {
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
      qc.invalidateQueries({ queryKey: ["admin", "package", name] });
    },
  });
}

/**
 * 切受管 skill 的 state(stop/yank 等场景)。
 * 需要先把 package.managed 切成 1,否则后端拒绝(409)。
 */
export function useSetPackageState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; state: SkillState; reason?: string }) =>
      hubFetch(`/admin/packages/${encodeURIComponent(args.name)}/state`, {
        method: "POST",
        body: JSON.stringify({ state: args.state, reason: args.reason }),
      }),
    onSuccess: (_d, { name }) => {
      qc.invalidateQueries({ queryKey: ["admin", "packages"] });
      qc.invalidateQueries({ queryKey: ["admin", "package", name] });
    },
  });
}
